import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRequest } from "../hooks/useRequest";
import { getPlans, Plan } from "../api/subscription";
import ContactModal from "../components/ContactModal";
import ConsoleHeader from "../layout/ConsoleHeader";
import "./Pricing.scss";

// 商家 logo 从 public/merchant-logos/ 以真实文件形式提供（不走 webpack import）。
// 原来用 import 引入的小图（<10KB）会被 CRA 内联成 base64 data URI，导致：
//   ① <img src> 里塞满 base64，页面体积暴涨；
//   ② JSON-LD 的 logo 变成 `${origin}data:image...` 这种无法抓取的坏 URL。
// 改为 public 下的真实路径后，<img> 与 JSON-LD 都拿到可抓取的绝对 URL。
const LOGO_BASE = `${process.env.PUBLIC_URL || ""}/merchant-logos`;

/** 支持的商家（logo 墙，顺序与交互稿一致） */
const MERCHANTS: { name: string; logo: string }[] = [
  { name: "Ashford", logo: `${LOGO_BASE}/ashford.png` },
  { name: "Foot Locker", logo: `${LOGO_BASE}/foot-locker.png` },
  { name: "Jomashop", logo: `${LOGO_BASE}/jomashop.png` },
  { name: "Michael Kors", logo: `${LOGO_BASE}/michael-kors.png` },
  { name: "Nordstrom Rack", logo: `${LOGO_BASE}/nordstrom-rack.png` },
  { name: "Saks Fifth Avenue", logo: `${LOGO_BASE}/saks-fifth-avenue.png` },
  { name: "SHOEBACCA", logo: `${LOGO_BASE}/shoebacca.png` },
  { name: "Urban Outfitters", logo: `${LOGO_BASE}/urban-outfitters.png` },
  { name: "Walgreens", logo: `${LOGO_BASE}/walgreens.png` },
  { name: "YOOX", logo: `${LOGO_BASE}/yoox.png` },
  { name: "Zappos", logo: `${LOGO_BASE}/zappos.png` },
];

/**
 * 商家 logo 墙的 JSON-LD（schema.org ItemList）。
 * 让爬虫理解：这是 CloudStore AI「支持的商家」的 logo 列表，
 * 每项是一个 Organization（商家）及其 logo 图片。
 */
function buildMerchantsJsonLd(): string {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  const data = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Supported merchants",
    description:
      "Logos of the premium US & EU merchants supported by CloudStore AI, all accessible through a single API.",
    numberOfItems: MERCHANTS.length,
    itemListElement: MERCHANTS.map((m, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Organization",
        name: m.name,
        logo: `${origin}${m.logo}`,
        description: `${m.name} is a merchant supported by CloudStore AI.`,
      },
    })),
  };
  return JSON.stringify(data);
}

interface PlanCard {
  planCode: string;
  name: string;
  price: string;
  features: string[];
  cta: "start" | "contact";
}

const ENTERPRISE: PlanCard = {
  planCode: "ENTERPRISE",
  name: "Enterprise Plan",
  price: "Custom pricing",
  features: [
    "Custom order & API call volume",
    "Volume discounts",
    "Dedicated account manager",
    "Priority support",
    "Custom integrations & onboarding",
  ],
  cta: "contact",
};

function priceLabel(feeCents: number): string {
  if (feeCents <= 0) return "$0 / month";
  return `$${(feeCents / 100).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })} / month`;
}

/**
 * 价格 JSON-LD（schema.org SoftwareApplication + Offer）。
 * 让爬虫理解 CloudStore API 的套餐价格：Free plan 与 Developer plan 各一个 Offer。
 * price 由接口的 monthlyFeeCents 派生（美分 → 两位小数美元字符串）。
 */
function buildPricingJsonLd(plans: Plan[]): string {
  const offers = plans.map((p) => ({
    "@type": "Offer",
    name: `${p.planName} plan`,
    price: (p.monthlyFeeCents / 100).toFixed(2),
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
  }));
  const data = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "CloudStore API",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "Catalog and Checkout APIs for 6,000+ fashion brands and 400+ US & EU merchants.",
    offers,
    provider: {
      "@type": "Organization",
      name: "CloudStore AI",
      url: "https://www.nubestore.ai/",
    },
  };
  return JSON.stringify(data);
}

/** 美分 → 美元字符串，去掉多余的 0（200 → "$2"，5 → "$0.05"） */
function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })}`;
}

/** 从 apiPricing 中取指定 apiCode 的单价（美分） */
function apiPriceCents(plan: Plan, apiCode: string): number | undefined {
  return plan.apiPricing?.find((p) => p.apiCode === apiCode)?.priceCents;
}

/** 各套餐写死的附加文案（接口未提供营销文案） */
const PLAN_EXTRA_FEATURES: Record<string, string[]> = {
  FREE: ["Email support"],
  DEVELOPER: [
    "Third-party platform costs with no markup",
    "Includes 24/7 email support",
  ],
};

function features(plan: Plan): string[] {
  const list: string[] = [
    `${dollars(plan.monthlyCreditsCents)} per month free credits`,
  ];
  const orderCents = apiPriceCents(plan, "checkout.order");
  if (orderCents != null) list.push(`${dollars(orderCents)} per order placed`);
  const fetchCents = apiPriceCents(plan, "product.detail");
  if (fetchCents != null) list.push(`${dollars(fetchCents)} per product fetch`);
  list.push(...(PLAN_EXTRA_FEATURES[plan.planCode] ?? []));
  return list;
}

/** Shopify App 链接 */
const SHOPIFY_APP_URL =
  "https://apps.shopify.com/cloud-store?st_source=autocomplete&surface_detail=autocomplete_apps";

/** FAQ 列表：问题 + 答案（答案可为多段/含列表或链接） */
const FAQS: { q: string; a: JSX.Element }[] = [
  {
    q: "What are the technical advantages of your API?",
    a: (
      <>
        <p className="cs-pricing__faq-p">
          Our API is the industry's only unified solution for global luxury
          fashion catalog ingestion and order placement.
        </p>
        <ul className="cs-pricing__faq-ul">
          <li>
            Unified Endpoint Suite: Supports both GET /catalog and POST /orders
            for major EU/US luxury brands.
          </li>
          <li>
            Production-Tested Scale: 10+ years of uptime handling 5M+ successful
            order payloads.
          </li>
          <li>
            Real-Time Data Sync: Low-latency synchronization for price and
            inventory, with coupon/promo logic computed directly into the
            markdown payload.
          </li>
          <li>
            Enterprise Reliability: High-availability, production-ready
            architecture
          </li>
        </ul>
      </>
    ),
  },
  {
    q: "Do you provide product and ordering APIs for categories other than fashion?",
    a: (
      <p className="cs-pricing__faq-p">
        Yes, we do. We support a wide range of product categories beyond
        fashion. Please contact our Sales Team directly to discuss your specific
        requirements and get access to the relevant APIs.
      </p>
    ),
  },
  {
    q: "What merchants and brands do you work with?",
    a: (
      <p className="cs-pricing__faq-p">
        Our network includes 400+ premium merchants and brands across the US and
        Europe, including major department stores like Macy's, Saks Fifth
        Avenue, and Harrods, as well as leading fashion and beauty brands such as
        Michael Kors, Zappos, and many more. We provide access to over 20 million
        SKUs across fashion, beauty, and luxury categories, with product
        information, pricing, and inventory refreshed daily.
      </p>
    ),
  },
  {
    q: "How does pricing work across different plans?",
    a: (
      <>
        <p className="cs-pricing__faq-p">
          The Free Plan gives you $2/month in credits with access to 3 core API
          endpoints (products, discover, checkout).
        </p>
        <p className="cs-pricing__faq-p">
          The Developer Plan at $99/month includes $50/month in credits at 5x
          lower per-call pricing, plus access to all endpoints.
        </p>
        <p className="cs-pricing__faq-p">
          The Enterprise Plan offers fully custom pricing with volume discounts,
          a dedicated account manager, and an allocated Forward-Deployed
          Engineer.
        </p>
      </>
    ),
  },
  {
    q: "Can I start with the Free Plan and upgrade later?",
    a: (
      <p className="cs-pricing__faq-p">
        Absolutely. You can upgrade from Free to Developer at any time — your
        upgrade takes effect immediately after payment, with a new billing cycle
        starting from your upgrade date and full monthly credits. Downgrades take
        effect at the end of your current billing cycle. Your integrations
        continue working without any changes throughout the process.
      </p>
    ),
  },
  {
    q: "How long does integration take?",
    a: (
      <p className="cs-pricing__faq-p">
        Most integrations are completed in 7 to 14 days. The process is
        straightforward: we connect to your existing platform (Shopify,
        WooCommerce, custom catalog, or any REST-compatible system), our team
        handles the API configuration, and you go live with access to our entire
        merchant network. Developer and Enterprise plan clients receive dedicated
        onboarding support to accelerate the process.
      </p>
    ),
  },
  {
    q: "I'm a Shopify user, is there a simpler solution?",
    a: (
      <p className="cs-pricing__faq-p">
        You can use our Shopify App for a simpler, one-click integration.{" "}
        <a
          className="cs-pricing__faq-link"
          href={SHOPIFY_APP_URL}
          target="_blank"
          rel="noreferrer"
        >
          Click here
        </a>{" "}
        to go.
      </p>
    ),
  },
];

/**
 * 读取预渲染注入的套餐引导数据（scripts/prerender-pricing.js 写入 window.__PRICING_PLANS__）。
 * 有则用作首帧初始数据，使客户端首帧与预渲染 HTML 一致、可安全 hydrate。
 */
function getInjectedPlans(): Plan[] | null {
  if (typeof window === "undefined") return null;
  const data = (window as unknown as { __PRICING_PLANS__?: Plan[] })
    .__PRICING_PLANS__;
  return Array.isArray(data) ? data : null;
}

/** URL 上的套餐参数名（指定默认高亮的套餐） */
const PLAN_PARAM = "plan";

/**
 * 读取 ?plan= 参数，兼容以下所有链接形式：
 *   /cloudstore-api/#/pricing?plan=free   （query 在 hash 内）
 *   /cloudstore-api/?plan=free#/pricing   （query 在 hash 之前）
 *   /cloudstore-api/?plan=free
 *   /cloudstore-api?plan=free
 * 优先级：hash 内的 query（react-router）→ 真实 URL 的 query → hash 里手动解析兜底。
 */
function getPlanParam(hashSearch: URLSearchParams): string {
  const fromHash = hashSearch.get(PLAN_PARAM);
  if (fromHash) return fromHash;
  if (typeof window !== "undefined") {
    // # 之前的真实 query（location.search）
    const fromSearch = new URLSearchParams(window.location.search).get(
      PLAN_PARAM
    );
    if (fromSearch) return fromSearch;
    // 兜底：手动解析 hash 里的 query（如 #/pricing?plan=free）
    const qIndex = window.location.hash.indexOf("?");
    if (qIndex >= 0) {
      const fromHashRaw = new URLSearchParams(
        window.location.hash.slice(qIndex + 1)
      ).get(PLAN_PARAM);
      if (fromHashRaw) return fromHashRaw;
    }
  }
  return "";
}

const Pricing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plans = useRequest(getPlans, [], { initialData: getInjectedPlans() });
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [contactOpen, setContactOpen] = useState(false);
  // 当前悬浮的套餐；无悬浮时回落到链接参数 ?plan=free|developer|enterprise 指定的默认高亮
  const [hovered, setHovered] = useState<string | null>(null);
  const [defaultHighlight, setDefaultHighlight] = useState("");

  useEffect(() => {
    // 与预渲染注入的 <title>（scripts/prerender-pricing.js）保持一致。
    document.title = "CloudStore API Pricing & Plans | CloudStore AI";
  }, []);

  useEffect(() => {
    setDefaultHighlight(getPlanParam(searchParams).toUpperCase());
  }, [searchParams]);

  const openContact = () => setContactOpen(true);

  // 高亮跟随悬浮，未悬浮时用链接参数指定的默认框
  const activeCode = hovered ?? (defaultHighlight || null);

  const cards: PlanCard[] = [
    ...(plans.data || []).map((p) => ({
      planCode: p.planCode,
      name: p.planName,
      price: priceLabel(p.monthlyFeeCents),
      features: features(p),
      cta: "start" as const,
    })),
    ENTERPRISE,
  ];

  return (
    <div className="cs-pricing">
      {/* 顶部导航：与控制台共用同一个 Header */}
      <ConsoleHeader />

      {/* Hero */}
      <section className="cs-pricing__hero">
        <span className="cs-pricing__badge">• Flexible Plans for Every Scale</span>
        <h1 className="cs-pricing__hero-title">
          Plans that scale with your business
        </h1>
        <p className="cs-pricing__hero-sub">
          From your first API call to billions in GMV — choose the plan that
          fits where you are, and grow with us.
        </p>
      </section>

      {/* 套餐 */}
      <section className="cs-pricing__plans-section">
        <h2 className="cs-pricing__section-title">Choose your plan</h2>
        <p className="cs-pricing__section-sub">
          All plans include access to our global fashion supply network. Start
          free and scale as you grow.
        </p>

        {plans.loading && <div className="cs-pricing__muted">Loading…</div>}
        {!plans.loading && plans.error && (
          <div className="cs-pricing__muted">{plans.error}</div>
        )}

        {!plans.loading && !plans.error && (
          <>
            {/* JSON-LD：向爬虫声明套餐价格（Free plan / Developer plan 各一个 Offer） */}
            {(plans.data || []).length > 0 && (
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: buildPricingJsonLd(plans.data || []),
                }}
              />
            )}
            <div
              className="cs-pricing__plans"
              onMouseLeave={() => setHovered(null)}
            >
              {cards.map((card) => {
                const active = card.planCode === activeCode;
                return (
                  <div
                    key={card.planCode}
                    className={`cs-pricing__plan${
                      active ? " cs-pricing__plan--popular" : ""
                    }`}
                    onMouseEnter={() => setHovered(card.planCode)}
                  >
                    <div className="cs-pricing__plan-name">{card.name}</div>
                    <div className="cs-pricing__plan-price">{card.price}</div>
                    <ul className="cs-pricing__features">
                      {card.features.map((f, i) => (
                        <li key={i} className="cs-pricing__feature">
                          <span className="cs-pricing__check">✓</span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    {card.cta === "contact" ? (
                      <button
                        type="button"
                        className="cs-pricing__plan-btn"
                        onClick={openContact}
                      >
                        Contact Sales
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`cs-pricing__plan-btn${
                          active ? " cs-pricing__plan-btn--primary" : ""
                        }`}
                        onClick={() => navigate("/dashboard")}
                      >
                        Get Started
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>

      {/* Supported merchants —— logo 墙（plans 与 FAQ 之间，白色背景） */}
      <section className="cs-pricing__merchants">
        {/* JSON-LD：向爬虫声明这是「支持的商家」的 logo 列表 */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: buildMerchantsJsonLd() }}
        />
        <h2 className="cs-pricing__section-title">Supported merchants</h2>
        <div className="cs-pricing__merchant-grid">
          {MERCHANTS.map((m) => (
            <div className="cs-pricing__merchant-cell" key={m.name}>
              <img
                className="cs-pricing__merchant-logo"
                src={m.logo}
                alt={`${m.name} logo — a merchant supported by CloudStore AI`}
                title={`${m.name} — supported merchant logo`}
                loading="lazy"
                width={120}
                height={40}
              />
            </div>
          ))}
          <div className="cs-pricing__merchant-cell cs-pricing__merchant-cell--more">
            <span className="cs-pricing__merchant-more">more…</span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="cs-pricing__faq">
        <div className="cs-pricing__faq-inner">
          <h2 className="cs-pricing__section-title">
            Frequently asked questions
          </h2>
          <p className="cs-pricing__section-sub">
            Everything you need to know about our plans and services.
          </p>
          <div className="cs-pricing__faq-list">
            {FAQS.map((faq, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="cs-pricing__faq-item">
                  <button
                    type="button"
                    className="cs-pricing__faq-q"
                    onClick={() => setOpenFaq(open ? null : i)}
                  >
                    <span>{faq.q}</span>
                    <span className="cs-pricing__faq-toggle">
                      {open ? "−" : "+"}
                    </span>
                  </button>
                  {open && <div className="cs-pricing__faq-a">{faq.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 底部 CTA */}
      <section className="cs-pricing__cta">
        <h2 className="cs-pricing__cta-title">
          Ready to scale your fashion business?
        </h2>
        <p className="cs-pricing__cta-sub">
          Join 500+ e-commerce merchants and brands already powered by
          CloudStore AI.
        </p>
        <button
          type="button"
          className="cs-pricing__cta-btn"
          onClick={openContact}
        >
          Contact Us
        </button>
      </section>

      {/* Contact Us 弹窗 */}
      <ContactModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
      />
    </div>
  );
};

export default Pricing;
